import * as ExcelJS from 'exceljs';
import saveAs from 'file-saver';

type UISchemaElement = {
  type?: string;
  label?: string;
  scope?: string;
  rule?: any;
  options?: {
    detail?: { elements?: UISchemaElement[] };
    [key: string]: any;
  };
  elements?: UISchemaElement[];
};

type SheetColumn = {
  key: string;
  label: string;
  role?: 'asset_static' | 'asset' | 'message' | 'param';
};

type DropdownOptionInfo = {
  values: string[];
  hasSchemaOptions: boolean;
  isSecret: boolean;
};

export type ProtocolSheetExcelTemplateOptions = {
  fileName?: string;
  sheetName?: string;
  staticColumns?: string[];
  excludedLabels?: string[];
  maxParamsPerMessage?: number;
  columnWidth?: number;
  instructionRows?: string[];
  dataSchema?: any;
  secretOptions?: string[];
  componentName?: string;
};

const DEFAULT_STATIC_COLUMNS = [
  'asset_name',
  'asset_ip',
  'asset_status',
  'component_name',
  'component_status',
];

const DEFAULT_EXCLUDED_LABELS = ['copy configuration'];
const DEFAULT_EXCLUDED_FIELD_TOKENS = new Set([
  'copyconfigurationfrom',
  'copyconfiguration',
]);

const COLOR_HEADER_BG = 'FFC7C7C7';
const COLOR_HEADER_FONT = 'FF000000';
const COLOR_INSTRUCTION_BG = 'FFF7F7F7';
const COLOR_EXAMPLE_FONT = 'FF999999';
const COLOR_EXAMPLE_BG = 'FFECEFF4';

const DEFAULT_INSTRUCTION_ROWS = [
  'Instruction 1: Begin entering data from row 11, with one complete row per message.',
  'Instruction 2: When adding parameters, do so by creating additional rows instead of columns. Each parameter for the same message should occupy a separate row.',
  'Instruction 3: For each parameter row within the same message, repeat the values for asset_name, component_name, and message_name.',
  'Instruction 4: Fill in the message fields on the first row of each message block. Additional parameter rows can leave these message-specific fields blank.',
  'Instruction 5: For fields with static schema options, select values from the provided dropdown menu.',
  'Instruction 6: If there are no schema options available, you can manually enter the values.',
];

const PARAM_COLUMN_SEPARATOR = '__';

const toSnakeCase = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeFieldToken = (value: string): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const resolveCanonicalPropertyKey = (properties: Record<string, any>, ...candidates: string[]): string => {
  const propertyKeys = Object.keys(properties || {});
  for (const candidate of candidates) {
    const token = normalizeFieldToken(candidate);
    if (!token) continue;
    const matched = propertyKeys.find((propKey) => normalizeFieldToken(propKey) === token);
    if (matched) return matched;
  }
  return '';
};

const capitalizeLabel = (value: string): string =>
  value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getScopeKey = (scope?: string): string => {
  const token = scope?.split('/').pop() ?? '';
  return toSnakeCase(token);
};

const controlLabel = (el: UISchemaElement): string => el.label?.trim() || getScopeKey(el.scope);

const isArrayObjectNode = (node: any): boolean =>
  Boolean(node && typeof node === 'object' && node.type === 'array' && node.items?.properties && typeof node.items.properties === 'object');

const buildParamColumnKey = (arrayKey: string, propKey: string): string =>
  `${arrayKey}${PARAM_COLUMN_SEPARATOR}${propKey}`;

const parseParamColumnKey = (key: string): { arrayKey: string; propKey: string } | null => {
  const splitAt = key.indexOf(PARAM_COLUMN_SEPARATOR);
  if (splitAt < 0) return null;
  const arrayKey = key.slice(0, splitAt);
  const propKey = key.slice(splitAt + PARAM_COLUMN_SEPARATOR.length);
  if (!arrayKey || !propKey) return null;
  return { arrayKey, propKey };
};

const isExcluded = (label: string, excludedLabels: string[]): boolean =>
  excludedLabels.some((ex) => label.toLowerCase().includes(ex.toLowerCase()));

const isExcludedFieldToken = (value: string): boolean =>
  DEFAULT_EXCLUDED_FIELD_TOKENS.has(normalizeFieldToken(value));

const getMessageSchemaInfo = (
  dataSchema?: any,
  preferredArrayKey?: string
): {
  messageArrayKey: string;
  messageProperties: Record<string, any>;
  required: string[];
  identifierKey: string;
  typeKey?: string;
  parameterArrayKeys: Set<string>;
} => {
  const rootProperties = dataSchema?.properties && typeof dataSchema.properties === 'object'
    ? dataSchema.properties
    : {};

  const candidateArrayKeys = Object.entries(rootProperties)
    .filter(([, node]: [string, any]) => isArrayObjectNode(node))
    .map(([key]) => key);

  const preferredFromUi = String(preferredArrayKey || '').trim();
  const hasPreferredInRoot = Boolean(preferredFromUi && rootProperties[preferredFromUi]);

  const messageArrayKey = preferredFromUi && candidateArrayKeys.includes(preferredFromUi)
    ? preferredFromUi
    : hasPreferredInRoot
      ? preferredFromUi
      : candidateArrayKeys[0] ?? '';

  const messageNode = rootProperties[messageArrayKey] ?? {};
  const messageProperties = messageNode?.items?.properties && typeof messageNode.items.properties === 'object'
    ? messageNode.items.properties
    : {};
  const required = Array.isArray(messageNode?.items?.required) ? messageNode.items.required : [];

  const identifierKey =
    required.find((key: string) => {
      const node = messageProperties[key];
      return node?.type === 'string' || Array.isArray(node?.type);
    }) ??
    Object.keys(messageProperties).find((key) => messageProperties[key]?.type === 'string') ??
    Object.keys(messageProperties)[0] ??
    '';

  const typeKey =
    Object.keys(messageProperties).find((key) => {
      const node = messageProperties[key];
      const values = normalizeOptionList(node?.enum ?? node?.oneOf ?? node?.anyOf ?? node?.allOf);
      return values.length > 0 && (key.toLowerCase().includes('type') || values.length <= 10);
    }) ??
    (messageProperties.type ? 'type' : undefined);

  const parameterArrayKeys = new Set(
    Object.entries(messageProperties)
      .filter(([, node]: [string, any]) => {
        if (!node || typeof node !== 'object') return false;
        if (node.type !== 'array') return false;
        return Boolean(node.items?.properties && typeof node.items.properties === 'object');
      })
      .map(([key]) => key)
  );

  return {
    messageArrayKey,
    messageProperties,
    required,
    identifierKey,
    typeKey,
    parameterArrayKeys,
  };
};

const isParameterControl = (el: UISchemaElement, dataSchema?: any): boolean => {
  const rawKey = getScopeKey(el.scope);
  const messageInfo = getMessageSchemaInfo(dataSchema);
  const key = resolveCanonicalPropertyKey(messageInfo.messageProperties, rawKey) || rawKey;

  if (messageInfo.parameterArrayKeys.has(key)) return true;
  return false;
};

const isMessageArrayControl = (el: UISchemaElement, dataSchema?: any): boolean => {
  const key = getScopeKey(el.scope);
  const details = el.options?.detail?.elements;
  const rootProperties = dataSchema?.properties && typeof dataSchema.properties === 'object'
    ? dataSchema.properties
    : {};
  const candidateArrayKeys = Object.entries(rootProperties)
    .filter(([, node]: [string, any]) => isArrayObjectNode(node))
    .map(([arrayKey]) => arrayKey);

  if (key && candidateArrayKeys.includes(key)) return true;
  if (!Array.isArray(details) || !details.length || !candidateArrayKeys.length) return false;

  const detailKeys = details
    .filter((d) => d.type === 'Control')
    .map((d) => getScopeKey(d.scope))
    .filter(Boolean);
  if (!detailKeys.length) return false;

  return candidateArrayKeys.some((arrayKey) => {
    const props = rootProperties[arrayKey]?.items?.properties ?? {};
    const propKeys = Object.keys(props);
    const overlap = detailKeys.filter((detailKey) => propKeys.includes(detailKey)).length;
    return overlap >= Math.max(1, Math.floor(detailKeys.length / 3));
  });
};

const applyHeaderStyle = (cell: ExcelJS.Cell) => {
  cell.font = { bold: true, size: 10, color: { argb: COLOR_HEADER_FONT } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_HEADER_BG } };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
};

const getMessageArrayControl = (elements: UISchemaElement[], dataSchema?: any): UISchemaElement | null => {
  const controlQueue: UISchemaElement[] = [];
  const collectControls = (items: UISchemaElement[] | undefined) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      if (item.type === 'Control') {
        controlQueue.push(item);
      }
      if (Array.isArray(item.elements)) {
        collectControls(item.elements);
      }
      const nestedDetail = item.options?.detail?.elements;
      if (Array.isArray(nestedDetail)) {
        collectControls(nestedDetail);
      }
    });
  };

  collectControls(elements);

  for (const el of controlQueue) {
    if (el.type !== 'Control') continue;
    if (isMessageArrayControl(el, dataSchema)) return el;
  }

  // Dynamic fallback: if schema heuristics miss, treat the first control with detail rows as message array.
  for (const el of controlQueue) {
    if (el.type !== 'Control') continue;
    const detailElements = el.options?.detail?.elements;
    if (Array.isArray(detailElements) && detailElements.length > 0) return el;
  }

  return null;
};

const collectControlElements = (
  elements: UISchemaElement[] | undefined,
  includeDetail = false,
  out: UISchemaElement[] = []
): UISchemaElement[] => {
  if (!Array.isArray(elements)) return out;

  elements.forEach((el) => {
    if (!el || typeof el !== 'object') return;

    if (el.type === 'Control') {
      out.push(el);
    }

    if (Array.isArray(el.elements)) {
      collectControlElements(el.elements, includeDetail, out);
    }

    const detailElements = el.options?.detail?.elements;
    if (includeDetail && Array.isArray(detailElements)) {
      collectControlElements(detailElements, includeDetail, out);
    }
  });

  return out;
};

const normalizeOptionList = (input: any): string[] => {
  if (!input) return [];

  if (Array.isArray(input)) {
    return input
      .map((item: any) => {
        if (item === null || item === undefined) return '';
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          return String(item);
        }
        if (typeof item === 'object') {
          return String(
            item.label ??
            item.title ??
            item.value ??
            item.const ??
            item.id ??
            item.key ??
            item.name ??
            ''
          );
        }
        return '';
      })
      .map((v: string) => v.trim())
      .filter(Boolean);
  }

  if (typeof input === 'object') {
    return normalizeOptionList(
      input.enumOptions ??
      input.values ??
      input.enums ??
      input.enum ??
      input.oneOf ??
      input.items ??
      input.options
    );
  }

  return [];
};

const mergeOptions = (
  map: Record<string, DropdownOptionInfo>,
  key: string,
  values: string[],
  source: 'ui' | 'schema',
  metadata?: { isSecret?: boolean }
) => {
  if (!key || !values.length) return;
  const existing = map[key] ?? { values: [], hasSchemaOptions: false, isSecret: false };
  map[key] = {
    values: Array.from(new Set([...existing.values, ...values])),
    hasSchemaOptions: existing.hasSchemaOptions || source === 'schema',
    isSecret: existing.isSecret || Boolean(metadata?.isSecret),
  };
};

const getSchemaNodeOptions = (node: any): { values: string[]; isSecret: boolean } => {
  if (!node || typeof node !== 'object') return { values: [], isSecret: false };

  const out: string[] = [];
  const isSecret = node.secret === true;

  // Direct enum list is a static constrained set.
  out.push(...normalizeOptionList(node.enum));

  // const is also a static constrained value.
  if (
    typeof node.const === 'string' ||
    typeof node.const === 'number' ||
    typeof node.const === 'boolean'
  ) {
    out.push(String(node.const));
  }

  // Static choices represented through oneOf / anyOf / allOf entries.
  const variants = [
    ...(Array.isArray(node.oneOf) ? node.oneOf : []),
    ...(Array.isArray(node.anyOf) ? node.anyOf : []),
    ...(Array.isArray(node.allOf) ? node.allOf : []),
  ];

  variants.forEach((variant: any) => {
    if (variant === null || variant === undefined) return;
    if (typeof variant === 'string' || typeof variant === 'number' || typeof variant === 'boolean') {
      out.push(String(variant));
      return;
    }
    if (typeof variant === 'object') {
      out.push(...normalizeOptionList(variant.enum));
      if (
        typeof variant.const === 'string' ||
        typeof variant.const === 'number' ||
        typeof variant.const === 'boolean'
      ) {
        out.push(
          String(
            variant.title ??
            variant.label ??
            variant.const
          )
        );
      }
    }
  });

  // Array schemas can define static enum lists under items.enum / items.oneOf.
  if (node.items && typeof node.items === 'object' && !Array.isArray(node.items)) {
    const itemInfo = getSchemaNodeOptions(node.items);
    out.push(...itemInfo.values);
    return {
      values: Array.from(new Set(out.map((v) => String(v).trim()).filter(Boolean))),
      isSecret: isSecret || itemInfo.isSecret,
    };
  }

  return {
    values: Array.from(new Set(out.map((v) => String(v).trim()).filter(Boolean))),
    isSecret,
  };
};

const getMessageRuleConstOptions = (
  messageControl: UISchemaElement | null,
  dataSchema?: any
): Record<string, DropdownOptionInfo> => {
  const map: Record<string, DropdownOptionInfo> = {};
  const details = messageControl?.options?.detail?.elements || [];
  const messageInfo = getMessageSchemaInfo(dataSchema, getScopeKey(messageControl?.scope));

  details
    .filter((d) => d.type === 'Control')
    .forEach((d) => {
      const condition = d.rule?.condition;
      const rawTargetKey = getScopeKey(condition?.scope);
      const targetKey = resolveCanonicalPropertyKey(messageInfo.messageProperties, rawTargetKey) || rawTargetKey;
      const constValue = condition?.schema?.const;
      if (targetKey && (typeof constValue === 'string' || typeof constValue === 'number' || typeof constValue === 'boolean')) {
        const targetNode = messageInfo.messageProperties?.[targetKey];
        const variants = [
          ...(Array.isArray(targetNode?.oneOf) ? targetNode.oneOf : []),
          ...(Array.isArray(targetNode?.anyOf) ? targetNode.anyOf : []),
          ...(Array.isArray(targetNode?.allOf) ? targetNode.allOf : []),
        ];

        const matchingVariant = variants.find(
          (variant: any) => String(variant?.const ?? '') === String(constValue)
        );

        const displayValue =
          matchingVariant?.title ??
          matchingVariant?.label ??
          String(constValue);

        mergeOptions(map, targetKey, [displayValue], 'ui');
      }
    });

  return map;
};

const collectDropdownOptions = (
  uiSchema: UISchemaElement,
  dataSchema?: any,
  secretOptions: string[] = []
): Record<string, DropdownOptionInfo> => {
  const dropdownMap: Record<string, DropdownOptionInfo> = {};
  const elements = Array.isArray(uiSchema?.elements) ? uiSchema.elements : [];
  const allControls = collectControlElements(elements, false);
  const messageControl = getMessageArrayControl(elements, dataSchema);
  const messageInfo = getMessageSchemaInfo(dataSchema, getScopeKey(messageControl?.scope));
  const rootProperties = dataSchema?.properties && typeof dataSchema.properties === 'object'
    ? dataSchema.properties
    : {};

  allControls.forEach((el) => {
    if (isMessageArrayControl(el, dataSchema)) return;

    const label = controlLabel(el);
    const uiKey = getScopeKey(el.scope) || toSnakeCase(label);
    const key = resolveCanonicalPropertyKey(rootProperties, uiKey, label) || uiKey;
    const options = normalizeOptionList(el.options);
    mergeOptions(dropdownMap, key, options, 'ui');

    const propNode = dataSchema?.properties?.[key];
    const schemaInfo = getSchemaNodeOptions(propNode);
    mergeOptions(dropdownMap, key, schemaInfo.values, 'schema', { isSecret: schemaInfo.isSecret });
    if (schemaInfo.isSecret) {
      mergeOptions(dropdownMap, key, secretOptions, 'ui', { isSecret: true });
    }
  });

  const details = messageControl?.options?.detail?.elements || [];
  details
    .filter((d) => d.type === 'Control')
    .forEach((d) => {
      const label = controlLabel(d);
      const key = getScopeKey(d.scope) || toSnakeCase(label);
      if (!key) return;

      if (isParameterControl(d, dataSchema)) {
        const canonicalParamKey = resolveCanonicalPropertyKey(messageInfo.messageProperties, key, label) || key;
        const paramArrayKey = messageInfo.parameterArrayKeys.has(canonicalParamKey)
          ? canonicalParamKey
          : Array.from(messageInfo.parameterArrayKeys)[0];
        if (!paramArrayKey) return;

        const paramProps = messageInfo.messageProperties[paramArrayKey]?.items?.properties ?? {};
        Object.entries(paramProps).forEach(([paramKey, paramNode]: [string, any]) => {
          const paramColumnKey = buildParamColumnKey(paramArrayKey, paramKey);
          const schemaInfo = getSchemaNodeOptions(paramNode);
          mergeOptions(dropdownMap, paramColumnKey, schemaInfo.values, 'schema', { isSecret: schemaInfo.isSecret });
          if (schemaInfo.isSecret) {
            mergeOptions(dropdownMap, paramColumnKey, secretOptions, 'ui', { isSecret: true });
          }
        });
        return;
      }

      const options = normalizeOptionList(d.options);
      const canonicalMessageKey =
        resolveCanonicalPropertyKey(messageInfo.messageProperties, key, label) || key;
      mergeOptions(dropdownMap, canonicalMessageKey, options, 'ui');

      const msgPropNode = messageInfo.messageProperties?.[canonicalMessageKey];
      const schemaInfo = getSchemaNodeOptions(msgPropNode);
      mergeOptions(dropdownMap, canonicalMessageKey, schemaInfo.values, 'schema', { isSecret: schemaInfo.isSecret });
      if (schemaInfo.isSecret) {
        mergeOptions(dropdownMap, canonicalMessageKey, secretOptions, 'ui', { isSecret: true });
      }
    });

  const ruleOptions = getMessageRuleConstOptions(messageControl, dataSchema);
  Object.entries(ruleOptions).forEach(([key, info]) =>
    mergeOptions(dropdownMap, key, info.values, info.hasSchemaOptions ? 'schema' : 'ui')
  );

  return dropdownMap;
};

const buildAssetColumns = (
  uiSchema: UISchemaElement,
  staticColumns: string[],
  excludedLabels: string[],
  dataSchema?: any
): SheetColumn[] => {
  const columns: SheetColumn[] = staticColumns.map((key) => ({
    key,
    label: key.replace(/_/g, ' '),
    role: 'asset_static',
  }));
  const seenNormalizedKeys = new Set(columns.map((c) => normalizeFieldToken(c.key)));
  const allControls = collectControlElements(Array.isArray(uiSchema?.elements) ? uiSchema.elements : [], false);
  const rootProperties = dataSchema?.properties && typeof dataSchema.properties === 'object'
    ? dataSchema.properties
    : {};

  allControls.forEach((el) => {

    const label = controlLabel(el);
    if (!label || isExcluded(label, excludedLabels)) return;
    if (isMessageArrayControl(el, dataSchema)) return;
    if (Array.isArray(el.options?.detail?.elements)) return;

    const uiKey = getScopeKey(el.scope) || toSnakeCase(label);
    const canonicalKey = resolveCanonicalPropertyKey(rootProperties, uiKey, label) || uiKey;
    const canonicalToken = normalizeFieldToken(canonicalKey);
    if (isExcludedFieldToken(canonicalKey) || isExcludedFieldToken(label)) return;

    if (!seenNormalizedKeys.has(canonicalToken)) {
      columns.push({ key: canonicalKey, label, role: 'asset' });
      seenNormalizedKeys.add(canonicalToken);
    }
  });

  return columns;
};

const buildMessageColumns = (
  messageArrayControl: UISchemaElement | null,
  dataSchema: any,
  excludedLabels: string[]
): SheetColumn[] => {
  const columns: SheetColumn[] = [];
  const addColumn = (key: string, label: string) => {
    if (!key) return;
    if (!columns.find((c) => c.key === key)) {
      columns.push({ key, label, role: parseParamColumnKey(key) ? 'param' : 'message' });
    }
  };

  if (messageArrayControl) {
    const messageInfo = getMessageSchemaInfo(dataSchema, getScopeKey(messageArrayControl.scope));
    const details = messageArrayControl.options?.detail?.elements || [];

    details
      .filter((d) => d.type === 'Control')
      .forEach((d) => {
        const label = controlLabel(d);
        if (!label || isExcluded(label, excludedLabels)) return;
        if (isParameterControl(d, dataSchema)) {
          const controlKey = getScopeKey(d.scope) || toSnakeCase(label);
          const paramArrayKey = messageInfo.parameterArrayKeys.has(controlKey)
            ? controlKey
            : Array.from(messageInfo.parameterArrayKeys)[0];
          const paramProps =
            paramArrayKey && messageInfo.messageProperties[paramArrayKey]?.items?.properties
              ? messageInfo.messageProperties[paramArrayKey].items.properties
              : undefined;
          const paramKeys = paramProps && typeof paramProps === 'object' ? Object.keys(paramProps) : [];
          if (paramKeys.length) {
            paramKeys.forEach((paramKey) => {
              addColumn(buildParamColumnKey(paramArrayKey, paramKey), `Param ${capitalizeLabel(paramKey)}`);
            });
          }
          return;
        }

        const key = getScopeKey(d.scope) || toSnakeCase(label);
        const canonicalMessageKey = resolveCanonicalPropertyKey(messageInfo.messageProperties, key, label) || key;
        addColumn(canonicalMessageKey, label);
      });
  }

  // Safety fallback for schemas lacking message detail controls.
  if (!columns.length) {
    const messageInfo = getMessageSchemaInfo(dataSchema);
    const messageKeys = Object.keys(messageInfo.messageProperties);

    messageKeys.forEach((key) => {
      if (messageInfo.parameterArrayKeys.has(key)) return;
      addColumn(key, capitalizeLabel(key));
    });

    const paramArrayKey = Array.from(messageInfo.parameterArrayKeys)[0];
    const paramProps =
      paramArrayKey && messageInfo.messageProperties[paramArrayKey]?.items?.properties
        ? messageInfo.messageProperties[paramArrayKey].items.properties
        : {};
    Object.keys(paramProps).forEach((paramKey) => {
      addColumn(buildParamColumnKey(paramArrayKey, paramKey), `Param ${capitalizeLabel(paramKey)}`);
    });
  }

  return columns;
};

const applyDropdownValidation = (
  worksheet: ExcelJS.Worksheet,
  letter: string,
  startRow: number,
  endRow: number,
  formula: string,
  strict: boolean,
  errorTitle: string,
  errorMessage: string,
  allowBlank = true
) => {
  const worksheetAny = worksheet as any;

  worksheetAny.dataValidations.add(`${letter}${startRow}:${letter}${endRow}`, {
    type: 'list',
    allowBlank,
    formulae: [formula],
    // Strict for non-secret static schema fields; non-strict for secrets/UI-sourced lists.
    showErrorMessage: strict,
    ...(strict ? { errorStyle: 'stop' } : {}),
    errorTitle,
    error: errorMessage,
  });
};

const applyDataValidations = (
  workbook: ExcelJS.Workbook,
  worksheet: ExcelJS.Worksheet,
  columns: SheetColumn[],
  startRow: number,
  dropdownMap: Record<string, DropdownOptionInfo>
) => {
  const endRow = 5000;
  const helperSheetName = '__validation_options';
  const helperSheet = workbook.getWorksheet(helperSheetName) ?? workbook.addWorksheet(helperSheetName);
  helperSheet.state = 'veryHidden';

  // Reset helper options for this generation cycle.
  helperSheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.value = null;
    });
  });

  let helperColumnCursor = 1;

  columns.forEach((col, index) => {
    const letter = worksheet.getColumn(index + 1).letter;
    if (!letter) return;

    const optionInfo = dropdownMap[col.key];
    if (optionInfo && optionInfo.values.length > 0) {
      const cleanValues = Array.from(
        new Set(optionInfo.values.map((v) => String(v).trim()).filter(Boolean))
      );
      if (!cleanValues.length) return;

      const helperColumn = helperSheet.getColumn(helperColumnCursor);
      cleanValues.forEach((value, valueIndex) => {
        helperSheet.getCell(valueIndex + 1, helperColumnCursor).value = value;
      });

      const helperLetter = helperColumn.letter;
      if (!helperLetter) return;
      const formula = `='${helperSheetName}'!$${helperLetter}$1:$${helperLetter}$${cleanValues.length}`;

      applyDropdownValidation(
        worksheet,
        letter,
        startRow,
        endRow,
        formula,
        optionInfo.hasSchemaOptions && !optionInfo.isSecret,
        `Invalid ${col.key}`,
        `Choose a value from the dropdown for ${col.key}`,
        true
      );
      helperColumnCursor += 1;
      return;
    }

    // No hardcoded fallbacks: dropdowns are derived from UI/data schema only.
  });
};

const generateDynamicExample = (
  key: string,
  dataSchema: any,
  repeatedContextKeys: Set<string>,
  parameterKeys: Set<string>,
  assetColumns: SheetColumn[],
  dropdownMap: Record<string, DropdownOptionInfo>,
  rowIndex: number = 1
): string => {
  const isParameterField = parameterKeys.has(key);

  // Instruction-aligned parameter row example: repeat only context fields; keep only param fields editable in row 2.
  if (rowIndex > 1 && !repeatedContextKeys.has(key) && !isParameterField) {
    return '';
  }

  const shouldKeepSameAcrossRows = !isParameterField;

  const exampleIndex = shouldKeepSameAcrossRows ? 1 : rowIndex;
  const lowerKey = key.toLowerCase();

  // IP should look like an IP sample even if schema format is missing.
  if (lowerKey.includes('ip')) {
    return `192.168.1.${exampleIndex}`;
  }

  // First priority: Check if dropdown has options (includes secrets merged from secretOptions)
  const optionInfo = dropdownMap[key];
  if (optionInfo?.values && optionInfo.values.length > 0) {
    // Filter out placeholder-like options
    const validOptions = optionInfo.values.filter(
      (v) => v && v.toLowerCase() !== 'select' && v.toLowerCase() !== 'please select'
    );
    if (validOptions.length > 0) {
      // For secrets or multi-option fields, use first valid option
      return validOptions[0];
    }
  }

  // Second priority: Check schema for type-based examples
  const isAssetField = assetColumns.some((c) => c.key === key);
  const messageInfo = getMessageSchemaInfo(dataSchema);
  const parsedParamKey = parseParamColumnKey(key);
  const schemaProp = isAssetField
    ? dataSchema?.properties?.[key]
    : parsedParamKey
      ? messageInfo.messageProperties?.[parsedParamKey.arrayKey]?.items?.properties?.[parsedParamKey.propKey]
      : messageInfo.messageProperties?.[key];

  const schemaType = Array.isArray(schemaProp?.type)
    ? schemaProp.type.find((t: string) => t !== 'null')
    : schemaProp?.type;
  const schemaFormat = String(schemaProp?.format ?? '').toLowerCase();

  if (schemaProp) {
    if (schemaFormat.includes('ipv4') || schemaFormat.includes('ipv6') || lowerKey.includes('ip')) {
      return `192.168.1.${exampleIndex}`;
    }

    // Number/integer: generate sequential examples
    if (schemaType === 'number' || schemaType === 'integer') {
      return String(exampleIndex);
    }

    // Boolean: generate boolean examples
    if (schemaType === 'boolean') {
      return exampleIndex === 1 ? 'true' : 'false';
    }

    if (schemaType === 'string') {
      if (key === 'asset_status' || key === 'component_status') {
        return exampleIndex === 1 ? 'enabled' : 'disabled';
      }

      if (repeatedContextKeys.has(key)) {
        return `${key}_${String(1).padStart(3, '0')}`;
      }

      if (isParameterField) {
        return `${key}_${String(rowIndex).padStart(3, '0')}`;
      }

      return `${key}_${String(exampleIndex).padStart(3, '0')}`;
    }
  }

  if (key === 'asset_status' || key === 'component_status') {
    return exampleIndex === 1 ? 'enabled' : 'disabled';
  }

  // Fallback: generic, deterministic value.
  return `${key}_${String(exampleIndex).padStart(3, '0')}`;
};

const applySheetLayout = (
  worksheet: ExcelJS.Worksheet,
  columns: SheetColumn[],
  instructionRows: string[],
  columnWidth: number,
  assetColumns: SheetColumn[],
  dataSchema: any,
  dropdownMap: Record<string, DropdownOptionInfo>,
  componentName?: string
) => {
  worksheet.columns = columns.map((c) => ({
    key: c.key,
    width: Math.max(columnWidth, c.label.length + 4),
  }));

  const messageInfo = getMessageSchemaInfo(dataSchema);
  const messageIdentifierColumn =
    columns.find((c) => c.role === 'message' && c.key === messageInfo.identifierKey) ??
    columns.find((c) => c.role === 'message');
  const messageIdentifierKey = messageIdentifierColumn?.key || messageInfo.identifierKey || 'message_identifier';

  const resolvedInstructionRows = instructionRows.map((line) =>
    line.replace(/message_name/g, messageIdentifierKey)
  );

  let rowCounter = 1;

  // Add component name header if provided
  if (componentName) {
    const componentRow = worksheet.getRow(rowCounter);
    const componentCell = componentRow.getCell(1);
    componentCell.value = `Component: ${componentName}`;
    componentCell.font = { bold: true, size: 12, color: { argb: 'FF000000' } };
    componentCell.alignment = { vertical: 'middle', horizontal: 'left' };
    if (columns.length > 1) {
      worksheet.mergeCells(rowCounter, 1, rowCounter, columns.length);
    }
    componentRow.height = 24;
    componentRow.commit();
    rowCounter++;
  }

  resolvedInstructionRows.forEach((line, index) => {
    const rowNumber = rowCounter + index;
    const row = worksheet.getRow(rowNumber);
    const cell = row.getCell(1);
    cell.value = line;
    cell.font = { size: 10, italic: true, color: { argb: 'FF444444' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_INSTRUCTION_BG } };
    if (columns.length > 1) {
      worksheet.mergeCells(rowNumber, 1, rowNumber, columns.length);
    }
    row.height = 22;
    row.commit();
  });

  const headerRowNo = rowCounter + resolvedInstructionRows.length;
  const exampleStartRowNo = headerRowNo + 1;
  const exampleRowsCount = 2;

  const repeatedContextKeys = new Set(
    ['asset_name', 'component_name', messageIdentifierKey].filter(Boolean) as string[]
  );
  const parameterKeys = new Set(columns.filter((c) => c.role === 'param').map((c) => c.key));

  const headerRow = worksheet.getRow(headerRowNo);
  columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = capitalizeLabel(col.label.replace(PARAM_COLUMN_SEPARATOR, ' '));
    applyHeaderStyle(cell);
  });
  headerRow.height = 24;
  headerRow.commit();

  for (let rowOffset = 0; rowOffset < exampleRowsCount; rowOffset++) {
    const rowNo = exampleStartRowNo + rowOffset;
    const row = worksheet.getRow(rowNo);
    columns.forEach((col, index) => {
      const cell = row.getCell(index + 1);
      cell.value = generateDynamicExample(
        col.key,
        dataSchema,
        repeatedContextKeys,
        parameterKeys,
        assetColumns,
        dropdownMap,
        rowOffset + 1
      );
      cell.font = { size: 9, italic: true, bold: true, color: { argb: COLOR_EXAMPLE_FONT } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_EXAMPLE_BG } };
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
    });
    row.height = 20;
    row.commit();
  }

  const frozenUntilRow = exampleStartRowNo + exampleRowsCount - 1;
  worksheet.views = [{ state: 'frozen', ySplit: frozenUntilRow, xSplit: 0 }];

  return { dataStartRow: frozenUntilRow + 1 };
};

const getDateTimeStamp = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}_${hh}-${min}-${ss}`;
};

export const generateAndDownloadProtocolSheetExcelTemplate = async (
  uiSchema: UISchemaElement,
  options: ProtocolSheetExcelTemplateOptions = {}
): Promise<string[]> => {
  const {
    fileName = 'asset_template',
    sheetName = 'Assets',
    staticColumns = DEFAULT_STATIC_COLUMNS,
    excludedLabels = DEFAULT_EXCLUDED_LABELS,
    maxParamsPerMessage = 3,
    columnWidth = 24,
    instructionRows = DEFAULT_INSTRUCTION_ROWS,
    dataSchema,
    secretOptions = [],
    componentName,
  } = options;

  const workbook = new ExcelJS.Workbook();
  const rootElements = Array.isArray(uiSchema?.elements) ? uiSchema.elements : [];
  const messageArrayControl = getMessageArrayControl(rootElements, dataSchema);
  const dropdownMap = collectDropdownOptions(uiSchema, dataSchema, secretOptions);

  const assetColumns = buildAssetColumns(uiSchema, staticColumns, excludedLabels, dataSchema);
  const messageColumns = buildMessageColumns(messageArrayControl, dataSchema, excludedLabels);
  const allColumns = [...assetColumns, ...messageColumns];

  const worksheet = workbook.addWorksheet(sheetName);
  const { dataStartRow } = applySheetLayout(
    worksheet,
    allColumns,
    instructionRows,
    columnWidth,
    assetColumns,
    dataSchema,
    dropdownMap,
    componentName
  );

  // Pre-fill component names for 10-20 rows if componentName is provided
  if (componentName) {
    const componentNameColIndex = allColumns.findIndex((col) => col.key === 'component_name');
    if (componentNameColIndex !== -1) {
      const prefillRows = Math.max(20, maxParamsPerMessage * 10);
      for (let i = 0; i < prefillRows; i++) {
        const rowNum = dataStartRow + i;
        const cell = worksheet.getCell(rowNum, componentNameColIndex + 1);
        cell.value = componentName;
      }
    }
  }

  applyDataValidations(workbook, worksheet, allColumns, dataStartRow, dropdownMap);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, `${fileName}_${getDateTimeStamp()}.xlsx`);

  return allColumns.map((col) => col.key);
};